import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import API_BASE_URL from "./config";
import "./Login.css";

const SuperAdminForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      // Call the Super Admin-specific endpoint
      const response = await fetch(`${API_BASE_URL}/api/super-admin/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      
      if (response.ok) {
        // Always navigate on 200 OK (includes enumeration prevention success)
        navigate("/super-admin-reset-password", {
          state: { email: email.trim().toLowerCase() },
          replace: true
        });
      } else {
        const data = await response.json();
        setError(data.message || data.error || "Failed to process request. Please try again.");
      }
    } catch (err) {
      console.error("Forgot password error:", err);
      setError("Network error. Please check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="auth-illustration">
        <div className="brand">
          <img
            className="brand-logo-img"
            src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`}
            alt="WeTano Solutions"
          />
          <div className="brand-text">
            <h2>WeTano Solutions</h2>
            <p>Super Admin Console</p>
          </div>
        </div>
        <ul className="highlights">
          <li>Secure password recovery for Super Admins</li>
          <li>OTP sent to your registered email</li>
          <li>Token expires after 15 minutes for security</li>
        </ul>
      </div>

      <div className="login-card">
        <div className="brand-mini">
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`}
            alt="WeTano Solutions"
          />
          <span>WeTano Solutions</span>
        </div>
        <div className="login-header">
          <h1>Forgot password</h1>
          <p>Enter your Super Admin email address.</p>
          <span className="sub-text">
            We'll send a one-time password (OTP) to reset your password.
          </span>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="sa-fp-email">Super Admin Email</label>
            <input
              type="email"
              id="sa-fp-email"
              name="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder="admin@example.com"
              required
              disabled={isLoading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button
            type="submit"
            className="login-button"
            disabled={isLoading}
          >
            {isLoading ? "Sending OTP..." : "Send OTP"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <Link to="/super-admin-login">← Back to Super Admin Login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export const SuperAdminResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || "";

  const [formData, setFormData] = useState({
    email: emailFromState,
    otp_code: "",
    new_password: "",
    confirm_password: "",
  });
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.confirm_password) {
      setError("Passwords do not match");
      return;
    }
    if (formData.new_password.length < 8) {
      setError("Super Admin password must be at least 8 characters");
      return;
    }
    if (!formData.email || !formData.otp_code) {
      setError("Email and OTP code are required");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const response = await fetch(`${API_BASE_URL}/api/super-admin/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: formData.email.trim().toLowerCase(),
          otp_code: formData.otp_code.trim(),
          new_password: formData.new_password,
        }),
      });
      const data = await response.json();

      if (response.ok) {
        navigate("/super-admin-login", {
          replace: true,
          state: { message: "Password reset successfully. You can now log in with your new password." },
        });
      } else {
        setError(data.message || data.error || "Failed to reset password");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="auth-illustration">
        <div className="brand">
          <img
            className="brand-logo-img"
            src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`}
            alt="WeTano Solutions"
          />
          <div className="brand-text">
            <h2>WeTano Solutions</h2>
            <p>Super Admin Console</p>
          </div>
        </div>
        <ul className="highlights">
          <li>Enter the OTP sent to your email</li>
          <li>Choose a strong new password (min. 8 characters)</li>
          <li>OTP is valid for 15 minutes only</li>
        </ul>
      </div>

      <div className="login-card">
        <div className="brand-mini">
          <img
            src={`${process.env.PUBLIC_URL || ''}/assets/wetano-logo.png`}
            alt="WeTano Solutions"
          />
          <span>WeTano Solutions</span>
        </div>
        <div className="login-header">
          <h1>Reset password</h1>
          <p>Enter the OTP sent to your Super Admin email and choose a new password.</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="sa-rp-email">Email</label>
            <input
              type="email"
              id="sa-rp-email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@example.com"
              required
              disabled={!!emailFromState}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sa-rp-otp">OTP code</label>
            <input
              type="text"
              id="sa-rp-otp"
              name="otp_code"
              value={formData.otp_code}
              onChange={handleChange}
              placeholder="6-digit"
              maxLength={6}
              required
              disabled={isLoading}
              className="otp-input"
            />
          </div>

          <div className="form-group">
            <label htmlFor="sa-rp-newpw">New password</label>
            <input
              type="password"
              id="sa-rp-newpw"
              name="new_password"
              value={formData.new_password}
              onChange={handleChange}
              placeholder="At least 8 characters"
              required
              disabled={isLoading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="sa-rp-confirmpw">Confirm password</label>
            <input
              type="password"
              id="sa-rp-confirmpw"
              name="confirm_password"
              value={formData.confirm_password}
              onChange={handleChange}
              placeholder="Re-enter new password"
              required
              disabled={isLoading}
            />
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="login-button" disabled={isLoading}>
            {isLoading ? "Resetting..." : "Reset password"}
          </button>
        </form>

        <div className="login-footer">
          <p>
            <Link to="/super-admin-forgot-password">Resend OTP</Link>
            {" · "}
            <Link to="/super-admin-login">← Back to login</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminForgotPassword;
